import { useActorRef, useSelector } from '@xstate/react';
import { useEffect, useRef } from 'react';
import type { SnapshotFrom } from 'xstate';
import type { JobResponse } from '~/types/hlx/jobResponse';
import { jobApplicationMachine, type ApplicationSource } from './jobApplication.machine';

type Snapshot = SnapshotFrom<typeof jobApplicationMachine>;
const selectSource = (state: Snapshot) => state.context.source;
const selectOpen = (state: Snapshot) => state.context.open;
const selectError = (state: Snapshot) => state.context.error;
const selectUncertain = (state: Snapshot) => state.context.uncertain;
const selectResult = (state: Snapshot) => state.context.result;
const selectBusy = (state: Snapshot) => state.matches('sending') || state.matches('recovering');

export const useJobApplication = (onApplied: (response: JobResponse) => void) => {
  const actor = useActorRef(jobApplicationMachine);
  const source = useSelector(actor, selectSource);
  const open = useSelector(actor, selectOpen);
  const error = useSelector(actor, selectError);
  const uncertain = useSelector(actor, selectUncertain);
  const result = useSelector(actor, selectResult);
  const busy = useSelector(actor, selectBusy);
  const callback = useRef(onApplied);
  const notified = useRef<JobResponse | null>(null);
  useEffect(() => {
    callback.current = onApplied;
  }, [onApplied]);
  useEffect(() => {
    if (result && result !== notified.current) {
      notified.current = result;
      callback.current(result);
    }
  }, [result]);

  return {
    source,
    open,
    error,
    uncertain,
    busy,
    openApplication: (source: ApplicationSource) => actor.send({ type: 'OPEN', source }),
    close: () => actor.send({ type: 'CLOSE' }),
    submit: () => actor.send({ type: 'SEND' }),
    retry: () => actor.send({ type: 'RETRY' }),
    refresh: () => actor.send({ type: 'REFRESH' }),
    discardRetry: () => actor.send({ type: 'DISCARD_RETRY' }),
  };
};
